import {useEffect, useMemo} from 'react'
import {usePathologiesSearchableList} from '@/features/projects/hooks/usePathologiesSearchableList'
import {MailerMultiSelectOption} from '@/features/mailer/components/mailer-multi-select/MailerMultiSelect'

interface UsePathologyOptionsProps {
    enabled: boolean
    /**
     * Notified with every loaded page so the parent can cache value→label pairs:
     * selected chips must keep their label even after the search results change.
     */
    onOptionsLoaded?: (options: MailerMultiSelectOption[]) => void
}

/**
 * Server-searched diagnoses options for the PRI pathology multi-selects.
 * The diagnoses domain is huge (11k+ records), so the full list can never be
 * loaded client-side: search and pagination stay on GET /diagnoses (contract §6).
 */
export const usePathologyOptions = ({enabled, onOptionsLoaded}: UsePathologyOptionsProps) => {
    const {pathologies, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage, onSearch} =
        usePathologiesSearchableList({limit: 25, enabled})

    const options = useMemo(
        () =>
            pathologies.map(diagnosis => ({
                value: diagnosis.id,
                label: diagnosis.description,
                prefix: diagnosis.code
            })),
        [pathologies]
    )

    useEffect(() => {
        if (options.length > 0) onOptionsLoaded?.(options)
    }, [options, onOptionsLoaded])

    return {
        options,
        onSearch,
        isLoading,
        isFetchingNextPage,
        hasNextPage,
        onLoadMore: fetchNextPage
    }
}
